/**
 * 🛡️ AdminCoachPayments — Dashboard admin justificatifs coach
 *
 * Vue globale des paiements versés aux coachs humains, avec :
 *   - KPIs (total versé, commission perçue, par méthode, top coachs)
 *   - Liste paginée filtrable
 *   - Détail modal (sessions + montants + bouton télécharger / renvoyer email)
 *   - Téléchargement PDF + audit log
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Download, Mail, FileText,
  Users, Euro, CreditCard, X, Eye, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const fmtMoney = (n) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtInt = (n) => new Intl.NumberFormat('fr-FR').format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso.slice(0, 10);
  }
};

const PAYMENT_METHOD_LABEL = {
  virement: 'Virement bancaire',
  paypal: 'PayPal',
};

function StatCard({ icon: Icon, label, value, sub, accent = '#D4AF37', testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-[#FAF7EE] border border-[#F1ECDC] p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}1A`, color: accent }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-semibold">
          {label}
        </span>
      </div>
      <div className="text-2xl sm:text-3xl font-light text-[#2A2319] tabular-nums">
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-[#6B7280]">{sub}</div>}
    </div>
  );
}

function ReceiptDetailModal({ receiptId, onClose, onDownload, onResend }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const API = getApiUrl();

  useEffect(() => {
    if (!receiptId) return;
    setLoading(true);
    fetch(`${API}/admin/coach-payment-receipts/${receiptId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => setData(json))
      .catch(() => toast.error('Impossible de charger le détail'))
      .finally(() => setLoading(false));
  }, [API, receiptId, token]);

  if (!receiptId) return null;

  return (
    <div
      data-testid="admin-coach-payment-detail-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#E8E2D2] rounded-xl max-w-3xl w-full my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#F1ECDC]">
          <h3 className="text-[#2A2319] font-light text-lg">
            {data?.receipt_number || 'Justificatif'}
          </h3>
          <button
            onClick={onClose}
            data-testid="admin-coach-payment-detail-close"
            className="text-[#6B7280] hover:text-[#2A2319] p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="p-8 text-[#6B7280] text-sm text-center">Chargement…</div>
        )}

        {data && (
          <div className="p-5 space-y-4">
            {/* Coach info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                  Coach
                </div>
                <div className="text-[#2A2319] font-semibold">
                  {data.coach_display_name}
                </div>
                <div className="text-[#4B5563] text-xs">{data.coach_email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                  Plan / Commission
                </div>
                <div className="text-[#2A2319]">
                  {(data.coach_subscription_plan || '—').toUpperCase()} ·{' '}
                  {data.commission_rate_display}%
                </div>
                <div className="text-[#4B5563] text-xs">
                  {PAYMENT_METHOD_LABEL[data.payment_method] || data.payment_method}
                </div>
              </div>
            </div>

            {/* Synthèse financière */}
            <div className="rounded-lg bg-[#FAF7EE]/80 border border-[#E8E2D2] p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#4B5563]">CA brut</span>
                  <span className="text-[#2A2319] tabular-nums">
                    {fmtMoney(data.total_generated)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4B5563]">Commission</span>
                  <span className="text-[#374151] tabular-nums">
                    − {fmtMoney(data.total_commission)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4B5563]">Net coach</span>
                  <span className="text-emerald-400 tabular-nums font-semibold">
                    {fmtMoney(data.net_amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#92710A] font-semibold">TOTAL VERSÉ</span>
                  <span className="text-[#92710A] tabular-nums font-bold">
                    {fmtMoney(data.amount_paid)}
                  </span>
                </div>
              </div>
            </div>

            {/* Sessions */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
                Sessions ({data.sessions_count})
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {(data.sessions || []).map((s) => (
                  <div
                    key={s.booking_id}
                    className="flex justify-between items-center text-xs bg-[#FAF7EE] border border-[#F1ECDC]"
                  >
                    <span className="text-[#374151]">
                      {fmtDate(s.date)} · {s.start_time}
                      {s.end_time ? `→${s.end_time}` : ''} · {s.theme_label}
                    </span>
                    <span className="text-[#2A2319] tabular-nums">
                      {fmtMoney(s.gross_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hash */}
            {data.integrity_hash && (
              <div className="text-[10px] text-[#9CA3AF] font-mono break-all">
                SHA-256 · {data.integrity_hash}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F1ECDC]">
              <button
                onClick={() => onDownload(data.receipt_id)}
                data-testid={`admin-coach-payment-download-${data.receipt_id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#B7901F] to-[#D4AF37] text-[#0A0A0B] font-semibold text-xs"
              >
                <Download className="w-3.5 h-3.5" /> Télécharger PDF
              </button>
              <button
                onClick={() => onResend(data.receipt_id)}
                data-testid={`admin-coach-payment-resend-${data.receipt_id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-[#E8E2D2] text-[#374151] hover:border-[#92710A]/60 text-xs"
              >
                <Mail className="w-3.5 h-3.5" /> Renvoyer email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCoachPayments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMethod, setFilterMethod] = useState('');
  const [openReceiptId, setOpenReceiptId] = useState(null);
  const { token } = useAuth();
  const API = getApiUrl();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMethod) params.set('payment_method', filterMethod);
      params.set('limit', '50');
      const [list, m] = await Promise.all([
        fetch(`${API}/admin/coach-payment-receipts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json()),
        fetch(`${API}/admin/coach-payment-receipts/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setMetrics(m);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [API, filterMethod, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const downloadPdf = async (receiptId) => {
    try {
      const r = await fetch(
        `${API}/admin/coach-payment-receipts/${receiptId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${receiptId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error(`Téléchargement impossible : ${e.message}`);
    }
  };

  const resendEmail = async (receiptId) => {
    try {
      const r = await fetch(
        `${API}/admin/coach-payment-receipts/${receiptId}/resend-email`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || `HTTP ${r.status}`);
      toast.success(`Email renvoyé à ${json.email_sent_to}`);
    } catch (e) {
      toast.error(`Échec renvoi : ${e.message}`);
    }
  };

  const T = metrics?.totals || {};
  const A = metrics?.activity || {};

  return (
    <div
      data-testid="admin-coach-payments-page"
      className="min-h-screen bg-[#FBF9F3] pt-16 sm:pt-8 pb-32 px-4"
    >
      <div className="max-w-6xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs text-[#6B7280] hover:text-[#374151] mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour Admin
        </Link>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#92710A] mb-1">
              Administration · Versements
            </p>
            <h1 className="text-xl sm:text-2xl font-light text-[#2A2319]">
              Paiements coachs
            </h1>
            <p className="text-xs text-[#6B7280] mt-1">
              Justificatifs émis lors de la confirmation de paiement coach
            </p>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="admin-coach-payments-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#E8E2D2] text-[#374151] hover:border-[#92710A]/60 text-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard
            testid="kpi-receipts-count"
            icon={FileText}
            label="Justificatifs émis"
            value={fmtInt(T.count)}
            sub={`${fmtInt(A.last_30_days)} sur 30 j`}
          />
          <StatCard
            testid="kpi-total-paid"
            icon={Euro}
            label="Total versé"
            value={fmtMoney(T.amount_paid)}
            sub={`CA brut ${fmtMoney(T.total_generated)}`}
            accent="#48825A"
          />
          <StatCard
            testid="kpi-commission"
            icon={CreditCard}
            label="Commission perçue"
            value={fmtMoney(T.total_commission)}
            sub="(plateforme)"
            accent="#7A4FCF"
          />
          <StatCard
            testid="kpi-coaches"
            icon={Users}
            label="Top coachs"
            value={fmtInt((metrics?.top_coaches || []).length)}
            sub="payés à ce jour"
            accent="#D4AF37"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            data-testid="admin-coach-payments-method-filter"
            className="px-3 py-2 bg-white border border-[#E8E2D2] rounded-lg text-[#2A2319] text-xs"
          >
            <option value="">Toutes méthodes</option>
            <option value="virement">Virement bancaire</option>
            <option value="paypal">PayPal</option>
          </select>
          <span className="text-[#6B7280] text-xs">{total} résultat(s)</span>
        </div>

        {/* List */}
        {!loading && items.length === 0 ? (
          <div
            data-testid="admin-coach-payments-empty"
            className="rounded-xl bg-[#FAF7EE] border border-[#F1ECDC] p-10 text-center"
          >
            <CreditCard className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
            <p className="text-[#4B5563] text-sm">
              Aucun justificatif émis pour le moment.
            </p>
            <p className="text-[#9CA3AF] text-xs mt-1">
              Les justificatifs apparaîtront ici dès la première confirmation de paiement.
            </p>
          </div>
        ) : (
          <div data-testid="admin-coach-payments-list" className="space-y-2">
            {items.map((r) => (
              <motion.div
                key={r.receipt_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`admin-coach-payment-row-${r.receipt_id}`}
                className="rounded-xl bg-[#FAF7EE] border border-[#F1ECDC] p-4 hover:border-[#D4AF37]/30 transition cursor-pointer"
                onClick={() => setOpenReceiptId(r.receipt_id)}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[11px] font-mono text-[#92710A]">
                        {r.receipt_number}
                      </span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-950/40 text-emerald-400 border border-emerald-900/40">
                        ✓ Payé
                      </span>
                      {!r.email_sent_at && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-950/40 text-amber-400 border border-amber-900/40">
                          Email non envoyé
                        </span>
                      )}
                    </div>
                    <div className="text-[#2A2319] text-sm font-semibold">
                      {r.coach_display_name}{' '}
                      <span className="text-[#6B7280] font-normal">· {r.coach_email}</span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <span>{fmtDate(r.paid_at || r.issued_at)}</span>
                      <span>· {r.sessions_count} session(s)</span>
                      <span>· {PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}</span>
                      <span>· Commission {r.commission_rate_display}%</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[#2A2319] font-semibold tabular-nums">
                      {fmtMoney(r.amount_paid)}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">
                      sur {fmtMoney(r.total_generated)} CA
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenReceiptId(r.receipt_id);
                      }}
                      className="mt-2 inline-flex items-center gap-1 text-[#92710A] hover:text-[#F3D060] text-xs"
                    >
                      <Eye className="w-3 h-3" /> Détail
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="text-xs text-[#9CA3AF] text-center mt-8">
          📌 Documents internes émis par AMSY E-COM SAS · Versements coachs séparés des factures clients · Conservation 10 ans
        </div>
      </div>

      {openReceiptId && (
        <ReceiptDetailModal
          receiptId={openReceiptId}
          onClose={() => setOpenReceiptId(null)}
          onDownload={downloadPdf}
          onResend={resendEmail}
        />
      )}
    </div>
  );
}
